/**
 * Iter 67en+++++ — Slug-specific overrides for high-value airport landing
 * pages. Allows us to swap in IATA-code-rich copy and emit a richer
 * schema.org `LocalBusiness` + `Airport` JSON-LD graph for the few
 * pages that account for the bulk of organic traffic, without
 * duplicating the entire LocationLandingPage component.
 *
 * Right now we only override the **Tirana International Airport Nënë
 * Tereza** slug; pattern is generic enough to add Saranda port,
 * Mother Teresa Square, etc. later by appending more entries.
 *
 * Each override returns:
 *  - `h1`           — full official airport name + IATA code
 *  - `subtitle`     — keyword-rich one-sentence pitch with arrivals copy
 *  - `seoTitle`     — <title> tag (≤65 chars)
 *  - `seoDescription` — <meta description> (≤220 chars)
 *  - `benefitCards` — three airport-specific benefit blurbs (no
 *                     repeating generic "Low deposit" copy)
 *  - `schemaGraph`  — array of @graph entries to inject into the page's
 *                     JSON-LD. Caller merges these with the generic
 *                     Place + BreadcrumbList entries.
 */
import { Plane, Clock, MapPinned, ShieldCheck, Tag, CalendarClock } from "lucide-react";

const TIRANA_AIRPORT = {
  iata: "TIA",
  icao: "LATI",
  officialName: "Tirana International Airport Nënë Tereza",
  city: "Tirana",
  country: "Albania",
  // Geo: Rinas village, Krujë municipality, ~11 km NW of Tirana.
  lat: 41.4147,
  lon: 19.7206,
  address: {
    streetAddress: "Rinas, 1051",
    addressLocality: "Tirana",
    addressRegion: "Krujë",
    postalCode: "1051",
    addressCountry: "AL",
  },
};

export const SLUG_OVERRIDES = {
  "tirana-airport-albania": {
    isAirport: true,
    airportMeta: TIRANA_AIRPORT,
    h1: "Car rental at Tirana International Airport Nënë Tereza (TIA)",
    subtitle: (
      "Land at TIA and drive away in minutes. RentalCar.al connects you with " +
      "trusted local hosts at Tirana International Airport Nënë Tereza (TIA / LATI) — " +
      "meet-and-greet right outside arrivals 24/7, no credit card hold, low or no " +
      "deposit, and free cancellation up to 24h before pickup. Whether you're heading " +
      "south to Saranda, Ksamil and the Albanian Riviera or north to Shkodër and the " +
      "Albanian Alps, your car is ready when you step out of the terminal."
    ),
    seoTitle: "TIA Car Rental · Tirana Airport (Nënë Tereza) — RentalCar.al",
    seoDescription: (
      "Rent a car at Tirana International Airport Nënë Tereza (TIA) with trusted local " +
      "hosts. 24/7 meet-and-greet at arrivals, no credit card hold, low or no deposit, " +
      "free cancellation up to 24h. Drive to Saranda, Berat, Ksamil & beyond — RentalCar.al."
    ),
    benefitCards: [
      {
        icon: Plane,
        title: "Pickup at TIA arrivals · 24/7",
        text: (
          "Your host meets you curbside right outside the Tirana International Airport " +
          "Nënë Tereza (TIA) arrivals hall. No shuttle, no off-airport transfer, no " +
          "queuing at a counter — be on the road within 10 minutes of clearing customs."
        ),
        bg: "from-amber-50 to-white",
        ring: "ring-amber-200",
        fg: "text-amber-700",
      },
      {
        icon: ShieldCheck,
        title: "No credit card hold at TIA",
        text: (
          "Skip the €1,500–€3,000 credit-card pre-authorisation airport counters demand. " +
          "Most RentalCar.al hosts at TIA accept debit cards and a small refundable " +
          "deposit — or zero deposit with our Full Protection add-on."
        ),
        bg: "from-emerald-50 to-white",
        ring: "ring-emerald-200",
        fg: "text-emerald-700",
      },
      {
        icon: CalendarClock,
        title: "Flight delayed? Free 24h cancellation",
        text: (
          "Plans change mid-air. Cancel or modify your TIA reservation free of charge " +
          "up to 24h before pickup. Land late at night? Your host monitors flight numbers " +
          "and adjusts the meet-time automatically."
        ),
        bg: "from-violet-50 to-white",
        ring: "ring-violet-200",
        fg: "text-violet-700",
      },
    ],
    /**
     * Schema.org @graph fragments specific to Tirana Airport. The generic
     * LandingPage caller already emits Place + BreadcrumbList, so we add:
     *  • Airport     — official IATA/ICAO codes + geo coordinates so
     *                  Google can match arrivals-intent queries.
     *  • LocalBusiness (CarRental) — tells Google we operate a
     *                  car-rental service at this airport with
     *                  24/7 availability.
     */
    schemaGraph: () => [
      {
        "@type": "Airport",
        name: TIRANA_AIRPORT.officialName,
        iataCode: TIRANA_AIRPORT.iata,
        icaoCode: TIRANA_AIRPORT.icao,
        address: {
          "@type": "PostalAddress",
          ...TIRANA_AIRPORT.address,
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: TIRANA_AIRPORT.lat,
          longitude: TIRANA_AIRPORT.lon,
        },
      },
      {
        "@type": ["LocalBusiness", "CarRental"],
        name: "RentalCar.al — Tirana Airport (TIA)",
        description:
          "Car rental service at Tirana International Airport Nënë Tereza (TIA) with 24/7 meet-and-greet pickup, no credit card hold, and free cancellation up to 24 hours.",
        url: typeof window !== "undefined"
          ? `${window.location.origin}/car-rental/tirana-airport-albania`
          : "https://www.rentalcar.al/car-rental/tirana-airport-albania",
        address: {
          "@type": "PostalAddress",
          ...TIRANA_AIRPORT.address,
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: TIRANA_AIRPORT.lat,
          longitude: TIRANA_AIRPORT.lon,
        },
        // 24/7 — opening hours spec for "always open".
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "00:00",
            closes: "23:59",
          },
        ],
        areaServed: {
          "@type": "Country",
          name: "Albania",
        },
      },
    ],
  },
};

export function getSlugOverride(slug) {
  return SLUG_OVERRIDES[slug] || null;
}

// Re-exporting the lucide icons used in benefit cards so this module
// stays self-contained on the consumer side.
export { Tag, MapPinned, Clock };
